/**
 * ClientPortalUpcomingExpirationsChart Hook
 *
 * Renders a 5-column chart showing lease expirations by year:
 * 4 individual upcoming years + 1 aggregate column for everything beyond.
 */

import { patchTooltipDelay } from "../utils/highcharts_tooltip_delay"
import { applyHighchartsTheme } from "../utils/highcharts_theme"

const PRIMARY_COLOR = "#0086e6"
const TRACK_COLOR = "rgba(0, 134, 230, 0.08)"

export const ClientPortalUpcomingExpirationsChart = {
  mounted() {
    const chartData = JSON.parse(this.el.dataset.chartData || "[]")
    const displayMode = this.el.dataset.displayMode || "area"
    const i18n = JSON.parse(this.el.dataset.i18n || "null") || window.I18n || {}
    this.renderChart(chartData, displayMode, i18n)

    this.handleEvent(
      "update-upcoming-expirations-chart",
      ({ data, display_mode }) => {
        this.renderChart(data, display_mode, i18n)
      },
    )
  },

  renderChart(chartData, displayMode, i18n) {
    const t = (key) => i18n[key]

    import("highcharts").then((Highcharts) => {
      patchTooltipDelay(Highcharts.default)
      applyHighchartsTheme(Highcharts.default)

      const categories = chartData.map((d) =>
        d.is_aggregate ? `${d.year}+` : d.year.toString(),
      )
      const totalSize = chartData.reduce((sum, d) => sum + d.total_size, 0)
      const isPercent = displayMode === "percent"

      const maxValue =
        Math.max(
          ...chartData.map((d) =>
            isPercent
              ? totalSize > 0
                ? (d.total_size / totalSize) * 100
                : 0
              : d.total_size,
          ),
        ) * 1.4

      const formatValue = (value) => {
        if (value >= 1000000) {
          return (value / 1000000).toFixed(1) + "M"
        } else if (value >= 1000) {
          return Math.round(value / 1000) + "k"
        }
        return isPercent
          ? Highcharts.default.numberFormat(value, 1) + "%"
          : Math.round(value).toString()
      }

      const valueSeries = []
      const restSeries = []

      chartData.forEach((d) => {
        const rawValue = isPercent
          ? totalSize > 0
            ? (d.total_size / totalSize) * 100
            : 0
          : d.total_size

        valueSeries.push({
          y: rawValue,
          totalSize: d.total_size,
          color: PRIMARY_COLOR,
          custom: {
            locations: d.locations || [],
            locationCount: d.location_count ?? (d.locations || []).length,
            year: d.year,
            isAggregate: !!d.is_aggregate,
          },
        })
        restSeries.push({
          y: maxValue - rawValue,
          color: TRACK_COLOR,
        })
      })

      Highcharts.default.chart(this.el.id, {
        chart: {
          type: "column",
          backgroundColor: "transparent",
          spacingTop: 28,
        },
        title: { text: null },
        xAxis: {
          categories: categories,
          lineWidth: 0,
          tickWidth: 0,
          labels: {
            style: {
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
            },
          },
        },
        yAxis: {
          visible: false,
          max: maxValue,
          reversedStacks: false,
        },
        legend: { enabled: false },
        tooltip: {
          delayForDisplay: 500,
          useHTML: true,
          outside: true,
          backgroundColor: "white",
          borderColor: "rgba(138,148,153,0.5)",
          borderRadius: 4,
          borderWidth: 1,
          shadow: {
            offsetX: 2,
            offsetY: 4,
            width: 4,
            color: "rgba(0,0,0,0.25)",
          },
          style: {
            padding: "0",
          },
          shared: true,
          followPointer: true,
          formatter: function () {
            const valuePoint = this.points?.find(
              (p) => p.series.name === "Value",
            )?.point
            if (!valuePoint) return false

            const custom = valuePoint.custom || {}
            const locations = custom.locations || []
            const count = custom.locationCount ?? locations.length
            const year = custom.year
            const isAggregate = custom.isAggregate
            const barColor = valuePoint.color || PRIMARY_COLOR

            if (count === 0) return false

            const expiringLabel = isAggregate
              ? `${t("Expiring in")} ${year} ${t("or later")}`
              : `${t("Expiring in")} ${year}`

            if (locations.length === 0) {
              return `<div style="padding: 12px 16px;">
                <div style="color: ${barColor}; font-size: 14px; font-weight: 500;">
                  ${count.toLocaleString()} ${count === 1 ? t("Lease") : t("Leases")} ${expiringLabel}
                </div>
              </div>`
            }

            const columnCount = count > 10 ? 3 : count > 5 ? 2 : 1

            let html = `<div style="padding: 12px 16px; max-width: ${columnCount > 1 ? columnCount * 280 + "px" : "auto"};">
              <div style="color: ${barColor}; font-size: 14px; font-weight: 500; margin-bottom: 12px;">
                ${count} ${count === 1 ? t("Lease") : t("Leases")} ${expiringLabel}
              </div>
              <div style="columns: ${columnCount}; column-gap: 24px; max-height: 400px; overflow-y: auto;">`

            locations.forEach((loc, index) => {
              const name = loc.name || t("Unknown Property")
              const cityState = [loc.city, loc.state].filter(Boolean).join(", ")
              const expiresAt = loc.expires_at
                ? `${t("Expires on")} ${loc.expires_at}`
                : ""
              const size =
                loc.size && loc.area_unit_abbr
                  ? loc.size.toLocaleString() + " " + loc.area_unit_abbr
                  : ""
              const productType = loc.product_type || ""
              const sizeType = [size, productType].filter(Boolean).join(" | ")

              html += `<div style="break-inside: avoid; margin-bottom: ${index < locations.length - 1 ? "16px" : "0"};">
                <div style="font-size: 16px; font-weight: 600; color: #111827; margin-bottom: 2px;">
                  ${name}
                </div>
                ${cityState ? `<div style="font-size: 13px; color: #9CA3AF; margin-bottom: 2px;">${cityState}</div>` : ""}
                ${expiresAt ? `<div style="font-size: 13px; color: #6B7280; margin-bottom: 2px;">${expiresAt}</div>` : ""}
                ${sizeType ? `<div style="font-size: 13px; color: #6B7280;">${sizeType}</div>` : ""}
              </div>`
            })

            html += "</div></div>"
            return html
          },
        },
        plotOptions: {
          column: {
            stacking: "normal",
            grouping: false,
            borderWidth: 0,
            borderRadius: {
              radius: 6,
              scope: "stack",
              where: "all",
            },
            shadow: false,
            states: {
              hover: { brightness: 0.1 },
              inactive: { opacity: 1 },
            },
          },
        },
        series: [
          {
            name: "Value",
            data: valueSeries,
            dataLabels: { enabled: false },
          },
          {
            name: "Rest",
            data: restSeries,
            dataLabels: {
              enabled: true,
              align: "center",
              verticalAlign: "top",
              y: -22,
              crop: false,
              overflow: "allow",
              useHTML: true,
              formatter: function () {
                const valuePoint =
                  this.series.chart.series[0].data[this.point.index]
                const value = valuePoint?.y || 0
                const color = valuePoint?.color || "#374151"
                return `<span style="color: ${color}; font-size: 14px; font-weight: 500;">${formatValue(value)}</span>`
              },
            },
          },
        ],
        credits: { enabled: false },
      })
    })
  },
}
